// src/Board.js
import React, { useEffect, useState } from 'react';
import './Board.css';

import { ref, onValue, set, push } from 'firebase/database'; // Remove getDatabase import

const generateRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
};

const Board = ({ database }) => { // Accept database as a prop
    const [players, setPlayers] = useState([]); // State to store list of players
    const [highlightedPortal, setHighlightedPortal] = useState(null); // Track which portal is highlighted
    const [isRolling, setIsRolling] = useState(false); // State to handle dice roll animation
    const [diceResult, setDiceResult] = useState(null); // State to store dice result
    const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0); // Track the current player's turn
    const [newPlayerName, setNewPlayerName] = useState(''); // State to handle new player name input
    const [isBouncing, setIsBouncing] = useState(false); // State to manage bounce animation
    const boardSize = 10; // 10x10 board

    // Define the portals for snakes (red) and ladders (green)
    const snakePortals = {
        99: 10,
        95: 75,
        92: 88,
        87: 24,
        64: 60,
        62: 18,
        49: 11,
        46: 25,
        16: 6,
    };

    const ladderPortals = {
        2: 38,
        7: 14,
        8: 31,
        15: 26,
        21: 82,
        28: 84,
        36: 44,
        51: 67,
        71: 91,
        78: 98,
    };

    useEffect(() => {
        const playersRef = ref(database, 'players');

        // Listen for real-time updates from Firebase
        onValue(playersRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setPlayers(Object.values(data));
            }
        });
    }, [database]); // Ensure the effect runs when the database prop changes

    const handleDiceRoll = () => {
        if (players.length === 0) return; // No players, no roll

        setIsRolling(true);
        setDiceResult(null); // Reset dice result display during rolling

        setTimeout(() => {
            const diceRoll = Math.floor(Math.random() * 6) + 1; // Roll a dice (1-6)
            setDiceResult(diceRoll); // Update the dice result

            const currentPlayer = players[currentPlayerIndex];
            let newPosition = currentPlayer.position;

            // Animate the player moving step-by-step
            movePlayerStepByStep(currentPlayerIndex, newPosition, diceRoll);
        }, 1000); // 1-second animation delay
    };

    const movePlayerStepByStep = (index, currentPosition, stepsRemaining) => {
        if (stepsRemaining === 0) {
            // Check for a snake or ladder after the movement ends
            let finalPosition = currentPosition;

            if (snakePortals[finalPosition]) {
                finalPosition = snakePortals[finalPosition];
            } else if (ladderPortals[finalPosition]) {
                finalPosition = ladderPortals[finalPosition];
            }

            updatePlayerPosition(index, finalPosition);

            setIsRolling(false);
            setCurrentPlayerIndex((currentPlayerIndex + 1) % players.length); // Move to the next player's turn
            return;
        }

        // Increment the player's position by 1
        const nextPosition = currentPosition + 1;
        updatePlayerPosition(index, nextPosition);

        // Continue moving the player after a short delay
        setTimeout(() => movePlayerStepByStep(index, nextPosition, stepsRemaining - 1), 300); // Delay of 300ms per step
    };

    const updatePlayerPosition = (index, newPosition) => {
        const player = players[index];
        const playerRef = ref(database, `players/${player.id}`);
        set(playerRef, { ...player, position: newPosition });
    };

    const handlePortalClick = (portal) => {
        if (snakePortals[portal]) {
            setHighlightedPortal({ from: portal, to: snakePortals[portal] });
        } else if (ladderPortals[portal]) {
            setHighlightedPortal({ from: portal, to: ladderPortals[portal] });
        } else if (Object.values(snakePortals).includes(portal)) {
            const fromPortal = Object.keys(snakePortals).find(key => snakePortals[key] === portal);
            setHighlightedPortal({ from: parseInt(fromPortal), to: portal });
        } else if (Object.values(ladderPortals).includes(portal)) {
            const fromPortal = Object.keys(ladderPortals).find(key => ladderPortals[key] === portal);
            setHighlightedPortal({ from: parseInt(fromPortal), to: portal });
        } else {
            setHighlightedPortal(null);
        }

        // Trigger bounce animation
        setIsBouncing(true);
        setTimeout(() => setIsBouncing(false), 1000); // Stop bounce animation after 1 second
    };

    const handleAddPlayer = () => {
        if (newPlayerName.trim() !== '') {
            const newPlayerRef = push(ref(database, 'players')); // Create a new player in the database
            const newPlayer = {
                id: newPlayerRef.key,
                name: newPlayerName,
                position: 1, // Start position
                color: generateRandomColor(), // Assign a random color to the player
            };
            set(newPlayerRef, newPlayer); // Add new player to the database
            setNewPlayerName(''); // Clear the input field
        }
    };

    // Generate board cells with alternating left-to-right and right-to-left rows
    const cells = [];
    for (let row = boardSize; row > 0; row--) {
        const rowCells = [];
        for (let col = 1; col <= boardSize; col++) {
            const cellNumber =
                row % 2 === 0
                    ? (row - 1) * boardSize + (boardSize - col + 1)
                    : (row - 1) * boardSize + col;

            const isSnake = snakePortals[cellNumber] || Object.values(snakePortals).includes(cellNumber);
            const isLadder = ladderPortals[cellNumber] || Object.values(ladderPortals).includes(cellNumber);
            const colorClass = isSnake
                ? 'snake'
                : isLadder
                    ? 'ladder'
                    : '';

            const isHighlighted =
                highlightedPortal &&
                (highlightedPortal.from === cellNumber ||
                    highlightedPortal.to === cellNumber);

            const isDestinationPortal = Object.values(snakePortals).includes(cellNumber) ||
                Object.values(ladderPortals).includes(cellNumber);

            const playersAtCell = players.filter(player => player.position === cellNumber);

            rowCells.push(
                <div
                    key={cellNumber}
                    className={`cell ${colorClass} ${isHighlighted ? 'highlight' : ''} ${isBouncing && isHighlighted ? 'bounce' : ''} ${isDestinationPortal ? 'destination-portal' : ''}`}
                    onClick={() => handlePortalClick(cellNumber)}
                >
                    {cellNumber}
                    {playersAtCell.map((player, index) => (
                        <div
                            key={index}
                            className="player-icon"
                            style={{ backgroundColor: player.color }} // Apply the player's color
                        >
                            {player.name.charAt(0).toUpperCase()}
                        </div>
                    ))}
                </div>
            );
        }
        cells.push(...rowCells);
    }

    return (
        <div>
            <h1>Snakes and Ladders</h1>
            <div className="board">{cells}</div>
            <button
                onClick={handleDiceRoll}
                className={`roll-button ${isRolling ? 'rolling' : ''}`}
                disabled={isRolling}
            >
                {isRolling ? 'Rolling...' : 'Roll Dice'}
            </button>
            {diceResult !== null && <div className="dice-result">You rolled: {diceResult}</div>}

            {/* New player input field */}
            <div className="add-player">
                <input
                    type="text"
                    value={newPlayerName}
                    onChange={(e) => setNewPlayerName(e.target.value)}
                    placeholder="Enter name"
                />
                <button onClick={handleAddPlayer}>+</button>
            </div>
            <div className="player-list">
                {players.map((player, index) => (
                    <div key={index}>
                        {player.name} is at position {player.position}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Board;
